class SQuickAddModal extends HTMLElement {
  #ac;
  #fetchController;
  #dialog;
  #content;
  #status;
  #triggerElement;
  #productUrl = '';

  connectedCallback() {
    this.#dialog = this.querySelector('dialog');
    this.#content = this.querySelector('[data-quick-add-content]');
    this.#status = this.querySelector('[data-quick-add-status]');

    if (!this.#dialog || !this.#content) return;

    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.addEventListener('command', (event) => {
      if (event.command === '--open-product') this.#openProduct(event.source);
    }, opts);

    this.addEventListener('change', (event) => {
      this.#handleVariantChange(event);
    }, opts);

    this.addEventListener('submit', () => {
      if (this.#dialog?.open) this.#dialog.close();
    }, opts);

    this.#dialog.addEventListener('close', () => {
      this.#fetchController?.abort();
      this.removeAttribute('aria-busy');
      this.#restoreFocus();
    }, opts);

    this.#dialog.addEventListener('click', (event) => {
      if (event.target === this.#dialog) this.#dialog.close();
    }, opts);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#fetchController?.abort();
  }

  async #openProduct(source) {
    if (!(source instanceof HTMLElement)) return;

    const productUrl = source.dataset.productUrl;
    if (!productUrl) return;

    this.#triggerElement = source;
    this.#productUrl = productUrl;

    // Preferred path: build the picker from the card's embedded product data (no network request).
    const data = this.#readProductData(source);
    if (data) {
      this.#content.replaceChildren(this.#buildPicker(data));
      this.#announce('');
      this.#showDialog();
      return;
    }

    this.#setLoading();
    this.#showDialog();

    await this.#loadProduct(productUrl);
  }

  #readProductData(source) {
    const id = source.dataset.productJson;
    const node = id ? document.getElementById(id) : null;
    if (!node) return null;
    try {
      const data = JSON.parse(node.textContent || '');
      return Array.isArray(data?.variants) && data.variants.length ? data : null;
    } catch {
      return null;
    }
  }

  // Builds title, price, Standard/Custom size choice, option pills and an add-to-cart form.
  // The form sits inside <s-product-form>, which adds the line to the cart and opens the cart drawer.
  #buildPicker(data) {
    const labels = this.dataset;
    const el = (tag, className = '', text = '') => {
      const node = document.createElement(tag);
      if (className) node.className = className;
      if (text) node.textContent = text;
      return node;
    };
    const pillClass = 'relative inline-flex h-9 cursor-pointer items-center justify-center rounded-button border border-border bg-transparent px-4 text-xs tracking-wide text-on-surface-secondary hover:border-on-surface has-checked:border-on-surface has-checked:text-on-surface has-checked:font-medium has-focus-visible:outline-2 has-focus-visible:outline-offset-2 has-focus-visible:outline-accent motion-safe:transition-colors';

    const variants = data.variants;
    const options = Array.isArray(data.options) ? data.options : [];
    let current = variants.find((v) => v.id === data.selected) || variants.find((v) => v.available) || variants[0];
    const selected = [...(current.options || [])];
    const sizeIndex = options.findIndex((name) => String(name).toLowerCase() === 'size');

    const wrapper = el('div', 'flex flex-col gap-6');

    const header = el('div', 'flex flex-col gap-1');
    const title = el('a', 'text-base font-medium text-on-surface no-underline hover:underline', data.title || '');
    title.href = data.url || this.#productUrl;
    const price = el('p', 'text-sm font-medium text-on-surface');
    header.append(title, price);
    wrapper.append(header);

    const productForm = el('s-product-form', 'block');
    productForm.setAttribute('data-product-form', '');
    productForm.dataset.errorMessage = labels.cartError || '';
    const form = el('form', 'flex flex-col gap-5');
    form.method = 'post';
    form.action = '/cart/add';
    const idInput = el('input');
    idInput.type = 'hidden';
    idInput.name = 'id';
    form.append(idInput);

    const uid = `qa-${Date.now()}`;
    const sizeGroup = el('div', 'group/size flex flex-col gap-4');
    let customMessage = null;
    const optionFieldsets = [];

    // Standard / Custom size choice, matching the product page.
    if (sizeIndex >= 0) {
      const typeSet = el('fieldset', 'flex flex-col gap-2');
      typeSet.append(el('legend', 'mb-2 text-sm font-medium text-on-surface', labels.sizeTypeLabel || ''));
      const row = el('div', 'flex flex-wrap gap-2');
      [[labels.standardLabel, true], [labels.customLabel, false]].forEach(([text, checked]) => {
        const label = el('label', pillClass);
        const input = el('input', 'sr-only');
        input.type = 'radio';
        input.name = `properties[${labels.sizeTypeLabel}]`;
        input.value = text || '';
        input.checked = checked;
        if (!checked) input.dataset.sizeCustom = '';
        label.append(input, document.createTextNode(text || ''));
        row.append(label);
      });
      typeSet.append(row);
      sizeGroup.append(typeSet);
      customMessage = el('p', 'hidden border-s-2 border-accent bg-surface-secondary px-4 py-3 text-sm text-on-surface group-has-[[data-size-custom]:checked]/size:block', labels.customMessage || '');
      customMessage.setAttribute('role', 'status');
    }

    options.forEach((name, index) => {
      const values = [...new Set(variants.map((v) => v.options?.[index]).filter(Boolean))];
      if (values.length < 2 && options.length === 1 && values[0] === 'Default Title') return;
      const set = el('fieldset', 'flex flex-col gap-2');
      if (index === sizeIndex) set.classList.add('group-has-[[data-size-custom]:checked]/size:hidden');
      set.append(el('legend', 'mb-2 text-sm font-medium text-on-surface', name));
      const row = el('div', 'flex flex-wrap gap-2');
      values.forEach((value) => {
        const label = el('label', pillClass);
        const input = el('input', 'sr-only');
        input.type = 'radio';
        input.name = `${uid}-option-${index}`;
        input.value = value;
        input.checked = selected[index] === value;
        input.dataset.optionIndex = String(index);
        const text = el('span', '', value);
        label.append(input, text);
        row.append(label);
      });
      set.append(row);
      optionFieldsets.push(set);
      (index === sizeIndex ? sizeGroup : form).append(set);
    });

    if (sizeIndex >= 0) {
      if (customMessage) sizeGroup.append(customMessage);
      form.append(sizeGroup);
    }

    const error = el('p', 'text-sm text-sale');
    error.setAttribute('data-add-to-cart-error', '');
    error.setAttribute('role', 'alert');
    error.hidden = true;

    const submit = el('button', 'button-primary h-11 px-3 text-sm rounded-button w-full cursor-pointer');
    submit.type = 'submit';
    submit.name = 'add';
    form.append(submit, error);

    const details = el('a', 'text-center text-sm text-on-surface-secondary underline underline-offset-2 hover:text-on-surface', labels.detailsLabel || '');
    details.href = data.url || this.#productUrl;
    form.append(details);

    productForm.append(form);
    wrapper.append(productForm);

    const sync = () => {
      current = variants.find((v) => (v.options || []).every((value, i) => value === selected[i])) || current;
      idInput.value = String(current.id);
      price.textContent = current.price || '';
      if (current.compare) {
        const compare = el('s', 'ms-2 font-normal text-on-surface-secondary', current.compare);
        price.append(compare);
      }
      // Grey out values that are sold out given the other selected options.
      form.querySelectorAll('input[data-option-index]').forEach((input) => {
        const i = Number(input.dataset.optionIndex);
        const match = variants.find((v) => (v.options || []).every((value, j) => (j === i ? value === input.value : value === selected[j])));
        const soldOut = !match || !match.available;
        const label = input.closest('label');
        label?.classList.toggle('line-through', soldOut);
        label?.classList.toggle('opacity-50', soldOut);
      });
      const available = Boolean(current.available);
      submit.disabled = !available;
      submit.textContent = available ? (labels.addLabel || '') : (labels.soldOutLabel || '');
    };

    form.addEventListener('change', (event) => {
      const input = event.target;
      if (!(input instanceof HTMLInputElement) || input.dataset.optionIndex === undefined) return;
      event.stopPropagation();
      selected[Number(input.dataset.optionIndex)] = input.value;
      sync();
    });

    sync();
    return wrapper;
  }

  async #handleVariantChange(event) {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    const picker = target.closest('[data-quick-add-variant-picker]');
    if (!picker || !this.contains(picker)) return;

    const variantId = target.dataset.variantId || target.selectedOptions?.[0]?.dataset?.variantId;
    if (!variantId || !this.#productUrl) return;

    this.#syncVariantInput(variantId);

    const url = new URL(this.#productUrl, window.location.origin);
    url.searchParams.set('variant', variantId);

    await this.#loadProduct(url.toString(), { focusVariantId: variantId, keepContent: true });
  }

  async #loadProduct(productUrl, { focusVariantId = null, keepContent = false } = {}) {
    this.#fetchController?.abort();
    this.#fetchController = new AbortController();
    const { signal } = this.#fetchController;

    this.setAttribute('aria-busy', 'true');
    if (!keepContent) this.#setLoading();

    try {
      const response = await fetch(this.#themeUrl(productUrl), { signal });
      if (!response.ok) throw new Error(`Failed to load quick add product: ${response.status}`);

      const responseText = await response.text();
      signal.throwIfAborted();

      const html = new DOMParser().parseFromString(responseText, 'text/html');
      const quickAddSource = html.querySelector('[data-quick-add-source]');
      if (!quickAddSource) throw new Error('Quick add source was not found in product response.');

      const content = this.#prepareProductContent(quickAddSource);
      signal.throwIfAborted();

      this.#content.replaceChildren(content);
      this.#initializePaymentButtons();
      this.#announce('');
      this.removeAttribute('aria-busy');

      if (focusVariantId) this.#focusVariant(focusVariantId);
    } catch (error) {
      if (error.name === 'AbortError') return;

      console.error('[s-quick-add-modal] Product fetch failed:', error);
      this.removeAttribute('aria-busy');
      // Never leave the shopper stuck: if the popup can't load, open the full product page instead.
      if (!keepContent && this.#productUrl) {
        window.location.assign(this.#productUrl);
        return;
      }
      this.#setError();
    } finally {
      if (this.#fetchController?.signal === signal) this.#fetchController = null;
    }
  }

  #prepareProductContent(source) {
    const content = source.cloneNode(true);

    content.className = 'flex flex-col gap-6';
    content.removeAttribute('data-quick-add-source');

    for (const script of content.querySelectorAll('script')) script.remove();
    content.querySelector('#inventory-status')?.remove();

    for (const picker of content.querySelectorAll('s-variant-picker')) {
      const replacement = document.createElement('div');
      for (const attribute of picker.attributes) {
        if (attribute.name === 'class') continue;
        replacement.setAttribute(attribute.name, attribute.value);
      }
      replacement.className = picker.className;
      replacement.setAttribute('data-quick-add-variant-picker', '');
      while (picker.firstChild) replacement.appendChild(picker.firstChild);
      picker.replaceWith(replacement);
    }

    return content;
  }

  #initializePaymentButtons() {
    if (!this.#content?.querySelector('.shopify-payment-button, [data-shopify-payment-button], shopify-accelerated-checkout')) return;

    window.Shopify?.PaymentButton?.init?.();
  }

  #showDialog() {
    if (!this.#dialog?.open) this.#dialog.showModal();
  }

  #setLoading() {
    const loadingText = this.dataset.loadingText || '';
    this.#announce(loadingText);
    this.#content?.replaceChildren(this.#messageNode(loadingText));
  }

  // Unpublished themes (previews and the editor) must fetch their own product markup, not the live theme's.
  #themeUrl(productUrl) {
    const url = new URL(productUrl, window.location.origin);
    const theme = window.Shopify?.theme;
    if (theme?.id && theme.role !== 'main') url.searchParams.set('preview_theme_id', String(theme.id));
    return url.toString();
  }

  #setError() {
    const errorMessage = this.dataset.errorMessage || '';
    this.#announce(errorMessage);
    this.#content?.replaceChildren(this.#messageNode(errorMessage));
  }

  #messageNode(message) {
    const node = document.createElement('p');
    node.className = 'py-12 text-center text-sm text-on-surface-secondary';
    node.textContent = message;
    return node;
  }

  #announce(message) {
    if (this.#status) this.#status.textContent = message;
  }

  #syncVariantInput(variantId) {
    const form = this.#content?.querySelector('form');
    const input = form?.querySelector('input[name="id"]');
    if (input instanceof HTMLInputElement) input.value = variantId;
  }

  #focusVariant(variantId) {
    requestAnimationFrame(() => {
      for (const input of this.#content?.querySelectorAll('[data-variant-id]') || []) {
        if (input instanceof HTMLElement && input.dataset.variantId === variantId) {
          input.focus();
          return;
        }
      }
    });
  }

  #restoreFocus() {
    if (this.#triggerElement && typeof this.#triggerElement.focus === 'function') {
      this.#triggerElement.focus();
      this.#triggerElement = null;
    }
  }
}

if (!customElements.get('s-quick-add-modal')) {
  customElements.define('s-quick-add-modal', SQuickAddModal);
}
